import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from '../../animations/useReducedMotion';
import { ArrowRight, Play } from '../../design/icons';
import {
    applyFilmToe,
    evaluateCutPair,
    rollAngleDeg,
    softKnee,
    templateACoverZoom,
    whipProfileAt,
    type RelayShotSpec,
} from '../../lib/matchCut';
import './match-cut-relay.css';

const FPS = 25;
const BPM = 150;
const FRAMES_PER_SHOT = 25;

interface CareerRelayShot extends RelayShotSpec {
    place: string;
    years: string;
    superWord: string;
    videoTimeSec: number;
    motif: 'spokes' | 'grid' | 'nodes' | 'rays' | 'guard' | 'wafer';
}

const RELAY_SHOTS: readonly CareerRelayShot[] = [
    {
        id: '01',
        name: 'Infosys · Enterprise Systems',
        world: 'CRM & Schema Foundations',
        place: 'Mysuru, India',
        years: '2015–2018',
        superWord: 'SYSTEMS',
        videoTimeSec: 0,
        motif: 'spokes',
        template: 'A-overhead',
        startFrame: 0,
        endFrame: 25,
        rawAnchor: [0.5, 0.5],
        rawScale: 0.25,
        meanLuma: 0.42,
        actionAngleDeg: 24,
        roll: true,
        accent: '#7dd3fc',
        groundTint: '#0e141d',
    },
    {
        id: '02',
        name: 'adidas · Global Platform Scale',
        world: 'High-Throughput Commerce',
        place: 'Herzogenaurach, DE',
        years: '2018–2019',
        superWord: 'SCALE',
        videoTimeSec: 1.0,
        motif: 'grid',
        template: 'A-overhead',
        startFrame: 25,
        endFrame: 50,
        rawAnchor: [0.5, 0.5],
        rawScale: 0.25,
        meanLuma: 0.44,
        actionAngleDeg: 24,
        roll: true,
        accent: '#8fbbc5',
        groundTint: '#0e1619',
    },
    {
        id: '03',
        name: 'UMKC · Neural & Graph Theory',
        world: 'Master of Science · CS',
        place: 'Kansas City, USA',
        years: '2019–2020',
        superWord: 'THEORY',
        videoTimeSec: 2.0,
        motif: 'nodes',
        template: 'A-overhead',
        startFrame: 50,
        endFrame: 75,
        rawAnchor: [0.5, 0.5],
        rawScale: 0.25,
        meanLuma: 0.46,
        actionAngleDeg: 24,
        roll: true,
        accent: '#b6a5d8',
        groundTint: '#14111b',
    },
    {
        id: '04',
        name: 'LemonOne · Workflow Automation',
        world: 'Event-Driven Pipelines',
        place: 'Berlin, DE',
        years: '2020',
        superWord: 'FLOW',
        videoTimeSec: 3.0,
        motif: 'rays',
        template: 'A-overhead',
        startFrame: 75,
        endFrame: 100,
        rawAnchor: [0.5, 0.5],
        rawScale: 0.25,
        meanLuma: 0.48,
        actionAngleDeg: 24,
        roll: true,
        accent: '#dfc48a',
        groundTint: '#18150e',
    },
    {
        id: '05',
        name: 'Zalando SE · Guarded AI Agents',
        world: 'Production RETURN_CONTROL + Pull',
        place: 'Berlin, DE',
        years: '2020–Now',
        superWord: 'AGENTS',
        videoTimeSec: 4.0,
        motif: 'guard',
        template: 'A-overhead',
        startFrame: 100,
        endFrame: 125,
        rawAnchor: [0.5, 0.5],
        rawScale: 0.25,
        meanLuma: 0.5,
        actionAngleDeg: 24,
        roll: true,
        whipOut: [
            [1.1, 40, 60],
            [1.22, 95, 160],
            [1.32, 145, 280],
        ],
        accent: '#6ee7b7',
        groundTint: '#0d1715',
    },
    {
        id: '06',
        name: 'Sculptures & Workbench · Whip Lockup',
        world: 'Signal Loom · Stack Descent · Workbench',
        place: 'Berlin Lab',
        years: 'Hero Lockup',
        superWord: 'INSIDE OUT.',
        videoTimeSec: 7.4,
        motif: 'wafer',
        template: 'A-overhead',
        startFrame: 125,
        endFrame: 150,
        rawAnchor: [0.5, 0.5],
        rawScale: 0.25,
        meanLuma: 0.52,
        actionAngleDeg: 24,
        roll: true,
        whipIn: [
            [1.32, -145, 280],
            [1.18, -80, 140],
            [1.08, -32, 50],
            [1.02, -8, 12],
        ],
        accent: '#92bda5',
        groundTint: '#0d1512',
    },
];

const TOTAL_FRAMES = RELAY_SHOTS.length * FRAMES_PER_SHOT;

function drawWorldMotif(
    ctx: CanvasRenderingContext2D,
    shot: CareerRelayShot,
    cx: number,
    cy: number,
    radius: number,
    phase: number,
    alpha = 1,
) {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = shot.accent;
    ctx.fillStyle = shot.accent;

    ctx.lineWidth = 2.6;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.lineWidth = 1.2;
    ctx.setLineDash([6, 5]);
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 0.64, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    const handoffAngle = phase * Math.PI * 2;
    const vx = cx + Math.cos(handoffAngle) * radius;
    const vy = cy + Math.sin(handoffAngle) * radius;
    ctx.beginPath();
    ctx.arc(vx, vy, 5.5, 0, Math.PI * 2);
    ctx.fill();

    switch (shot.motif) {
        case 'spokes': {
            for (let i = 0; i < 8; i++) {
                const a = (i / 8) * Math.PI * 2 + phase * 0.6;
                ctx.lineWidth = 1.1;
                ctx.beginPath();
                ctx.moveTo(cx + Math.cos(a) * (radius * 0.22), cy + Math.sin(a) * (radius * 0.22));
                ctx.lineTo(cx + Math.cos(a) * (radius * 0.92), cy + Math.sin(a) * (radius * 0.92));
                ctx.stroke();
            }
            break;
        }
        case 'grid': {
            const step = radius * 0.36;
            ctx.lineWidth = 1;
            for (let d = -2; d <= 2; d++) {
                const off = d * step;
                const chord = Math.sqrt(Math.max(0, radius * radius - off * off));
                ctx.beginPath();
                ctx.moveTo(cx - chord, cy + off);
                ctx.lineTo(cx + chord, cy + off);
                ctx.moveTo(cx + off, cy - chord);
                ctx.lineTo(cx + off, cy + chord);
                ctx.stroke();
            }
            break;
        }
        case 'nodes': {
            const pts: [number, number][] = [];
            for (let i = 0; i < 6; i++) {
                const a = (i / 6) * Math.PI * 2 + phase * 0.5;
                pts.push([cx + Math.cos(a) * (radius * 0.58), cy + Math.sin(a) * (radius * 0.58)]);
            }
            ctx.lineWidth = 1;
            for (let i = 0; i < pts.length; i++) {
                for (let j = i + 1; j < pts.length; j++) {
                    ctx.beginPath();
                    ctx.moveTo(pts[i][0], pts[i][1]);
                    ctx.lineTo(pts[j][0], pts[j][1]);
                    ctx.stroke();
                }
            }
            pts.forEach(([px, py]) => {
                ctx.beginPath();
                ctx.arc(px, py, 3.5, 0, Math.PI * 2);
                ctx.fill();
            });
            break;
        }
        case 'rays': {
            for (let i = 0; i < 12; i++) {
                const a = (i / 12) * Math.PI * 2;
                const r0 = i % 2 === 0 ? radius * 0.3 : radius * 0.5;
                ctx.lineWidth = 1.4;
                ctx.beginPath();
                ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
                ctx.lineTo(cx + Math.cos(a) * (radius * 0.96), cy + Math.sin(a) * (radius * 0.96));
                ctx.stroke();
            }
            break;
        }
        case 'guard': {
            ctx.lineWidth = 1.8;
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
                const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
                const px = cx + Math.cos(a) * (radius * 0.52);
                const py = cy + Math.sin(a) * (radius * 0.52);
                if (i === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.closePath();
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(cx, cy, radius * 0.16, 0, Math.PI * 2);
            ctx.fill();
            break;
        }
        case 'wafer': {
            const die = radius * 0.24;
            ctx.lineWidth = 1.2;
            for (let gx = -2; gx <= 2; gx++) {
                for (let gy = -2; gy <= 2; gy++) {
                    if (Math.hypot(gx, gy) > 2.3) continue;
                    ctx.strokeRect(cx + gx * die - die * 0.42, cy + gy * die - die * 0.42, die * 0.84, die * 0.84);
                }
            }
            break;
        }
    }

    ctx.restore();
}

type StageView = 'relay' | 'film' | 'cuts';

export const MatchCutCareerRelay: React.FC = () => {
    const reducedMotion = useReducedMotion();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [viewMode, setViewMode] = useState<StageView>('film');
    const [frame, setFrame] = useState(0);
    const [playing, setPlaying] = useState(() => !reducedMotion);
    const [onionSkin, setOnionSkin] = useState(false);

    const { coverZoom } = useMemo(() => templateACoverZoom(20, FRAMES_PER_SHOT, FPS, 16 / 9), []);

    useEffect(() => {
        if (reducedMotion || viewMode !== 'relay') {
            setPlaying(false);
            return;
        }
        if (!playing) return;
        const timer = window.setInterval(() => {
            if (!document.hidden) {
                setFrame(prev => (prev + 1) % TOTAL_FRAMES);
            }
        }, 1000 / FPS);
        return () => window.clearInterval(timer);
    }, [playing, reducedMotion, viewMode]);

    const shotIdx = Math.min(RELAY_SHOTS.length - 1, Math.floor(frame / FRAMES_PER_SHOT));
    const activeShot = RELAY_SHOTS[shotIdx];
    const nextShot = RELAY_SHOTS[(shotIdx + 1) % RELAY_SHOTS.length];
    const frameInShot = frame - activeShot.startFrame;

    const rollDeg = rollAngleDeg(frameInShot, FRAMES_PER_SHOT, frame, {
        dps: 20,
        fps: FPS,
        maxFrames: FRAMES_PER_SHOT,
    });
    const whip = whipProfileAt(frameInShot, FRAMES_PER_SHOT, activeShot.whipOut, activeShot.whipIn);
    const cutQC = useMemo(
        () => evaluateCutPair(activeShot, nextShot, 1920, 1080),
        [activeShot, nextShot],
    );

    useEffect(() => {
        if (viewMode !== 'relay') return;
        const canvas = canvasRef.current;
        if (!canvas || /jsdom/i.test(navigator.userAgent)) return;
        let ctx: CanvasRenderingContext2D | null = null;
        try {
            ctx = canvas.getContext('2d');
        } catch {
            return;
        }
        if (!ctx) return;

        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = canvas.clientWidth || 720;
        const h = canvas.clientHeight || 405;
        if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const toeLift = applyFilmToe(0.04, 0.022);
        ctx.fillStyle = activeShot.groundTint;
        ctx.fillRect(0, 0, w, h);

        const pool = ctx.createRadialGradient(w / 2, h / 2, 8, w / 2, h / 2, Math.max(w, h) * 0.56);
        const kneeHighlight = softKnee(0.92, 0.8);
        pool.addColorStop(0, `${activeShot.accent}${Math.round(kneeHighlight * 28).toString(16).padStart(2, '0')}`);
        pool.addColorStop(1, `rgba(0,0,0,${(1 - toeLift).toFixed(2)})`);
        ctx.fillStyle = pool;
        ctx.fillRect(0, 0, w, h);

        const cx = w / 2;
        const cy = h / 2;
        const radius = h * activeShot.rawScale;
        const continuousPhase = frame / TOTAL_FRAMES;

        ctx.save();
        ctx.translate(cx + whip.dx * 0.15, cy + whip.dy * 0.15);
        ctx.scale(coverZoom * whip.scale, coverZoom * whip.scale);
        ctx.rotate((rollDeg * Math.PI) / 180);
        ctx.translate(-cx, -cy);

        ctx.strokeStyle = 'rgba(255,255,255,0.07)';
        ctx.lineWidth = 1;
        ctx.strokeRect(w * 0.12, h * 0.12, w * 0.76, h * 0.76);

        drawWorldMotif(ctx, activeShot, cx, cy, radius, continuousPhase, onionSkin ? 0.65 : 1);

        if (onionSkin) {
            drawWorldMotif(ctx, nextShot, cx, cy, h * nextShot.rawScale, continuousPhase + 0.04, 0.5);
        }
        ctx.restore();

        if (onionSkin) {
            ctx.strokeStyle = 'rgba(80, 250, 123, 0.55)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(cx, 0);
            ctx.lineTo(cx, h);
            ctx.moveTo(0, cy);
            ctx.lineTo(w, cy);
            ctx.stroke();
        }
    }, [viewMode, frame, activeShot, nextShot, coverZoom, rollDeg, whip, onionSkin]);

    const currentBeat = Math.floor(frame / 10) + 1;
    const totalBeats = TOTAL_FRAMES / 10;

    return (
        <section className="mc-relay" aria-label="Match-cut career relay montage">
            <div className="mc-relay__header">
                <div>
                    <span className="mc-relay__eyebrow">
                        Match-Cut Relay Montage <span className="mc-relay__chip">{BPM} BPM · Template A</span>
                    </span>
                    <h2 className="mc-relay__title">
                        One shape keeps moving. <em>The world changes at every cut.</em>
                    </h2>
                </div>
                <div className="mc-relay__controls">
                    <button
                        type="button"
                        className="mc-relay__btn tap-hit"
                        aria-pressed={viewMode === 'film'}
                        onClick={() => setViewMode(v => (v === 'film' ? 'relay' : 'film'))}
                    >
                        <Play size={13} />
                        <span>{viewMode === 'film' ? 'Live Canvas Relay' : 'Watch 10s Rendered Reel (MP4)'}</span>
                    </button>
                    {viewMode === 'relay' && (
                        <>
                            <button
                                type="button"
                                className="mc-relay__btn tap-hit"
                                aria-pressed={playing}
                                disabled={reducedMotion}
                                onClick={() => setPlaying(p => !p)}
                            >
                                {reducedMotion ? 'Reduced motion' : playing ? 'Pause relay' : 'Play 150 BPM'}
                            </button>
                            <button
                                type="button"
                                className="mc-relay__btn tap-hit"
                                aria-pressed={onionSkin}
                                onClick={() => setOnionSkin(o => !o)}
                            >
                                {onionSkin ? '✓ Onion-skin QC (50/50)' : 'Onion-skin QC (qc.py)'}
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        className="mc-relay__btn tap-hit"
                        aria-pressed={viewMode === 'cuts'}
                        onClick={() => setViewMode(v => (v === 'cuts' ? 'relay' : 'cuts'))}
                    >
                        {viewMode === 'cuts' ? 'Hide Cut Sheet' : 'Inspect Cut Sheet (qc.py)'}
                    </button>
                    <Link to="/labs/match-cut-lab" className="mc-relay__link tap-hit">
                        Open Match-Cut Lab <ArrowRight size={14} />
                    </Link>
                </div>
            </div>

            <div className="mc-relay__stage">
                {viewMode === 'film' ? (
                    <div className="mc-relay__viewport">
                        <video
                            ref={videoRef}
                            className="mc-relay__video"
                            controls
                            muted
                            loop
                            playsInline
                            autoPlay={!reducedMotion}
                            poster="/media/career-match-cut/poster.webp"
                            src="/media/career-match-cut/career-match-cut.mp4"
                            width={1280}
                            height={720}
                            aria-label="10-second 150 BPM Match-Cut Career & Systems Reel"
                        >
                            <track
                                kind="captions"
                                src="/media/career-match-cut/captions.vtt"
                                srcLang="en"
                                label="English text"
                            />
                        </video>
                    </div>
                ) : viewMode === 'cuts' ? (
                    <div className="mc-relay__cuts-sheet">
                        <div className="mc-relay__cuts-head">
                            <span>qc.py cuts — Outgoing last frame vs Incoming first frame across all 7 cuts (BT.709 luminance steps &amp; centre crosshairs)</span>
                            <a href="/media/career-match-cut/career-match-cut.mp4" download>Download MP4</a>
                        </div>
                        <img
                            src="/media/career-match-cut/cuts-qc.webp"
                            alt="Match-cut QC contact sheet showing outgoing and incoming frames across all 7 cuts with green registration crosshairs"
                            width={968}
                            height={1890}
                            loading="lazy"
                        />
                    </div>
                ) : (
                    <div className="mc-relay__viewport">
                        <canvas ref={canvasRef} className="mc-relay__canvas" aria-hidden="true" />
                        <div className="mc-relay__hud-top">
                            <span>{`SHOT ${activeShot.id} / 06 · ${activeShot.place.toUpperCase()}`}</span>
                            <span>{`BEAT ${String(currentBeat).padStart(2, '0')}/${totalBeats} · ZOOM_A ${coverZoom.toFixed(3)}× · ROLL ${rollDeg > 0 ? '+' : ''}${rollDeg.toFixed(1)}°`}</span>
                        </div>
                        <div className="mc-relay__super" aria-live="polite">
                            <span>{activeShot.superWord}</span>
                        </div>
                        <div className="mc-relay__hud-bottom">
                            <div>
                                <strong>{activeShot.name}</strong>
                                <span>{activeShot.world} · {activeShot.years}</span>
                            </div>
                            <div className="mc-relay__qc-badge">
                                {`Cut ${activeShot.id}→${nextShot.id}: drift ${cutQC.centreDriftPct}% · luma ${cutQC.lumaStepPct > 0 ? '+' : ''}${cutQC.lumaStepPct}%`}
                            </div>
                        </div>
                    </div>
                )}

                {/* 6-Shot Beat-Grid Relay Rail */}
                <div className="mc-relay__rail" role="tablist" aria-label="Match-cut relay shots">
                    {RELAY_SHOTS.map((shot, idx) => {
                        const isActive = idx === shotIdx;
                        return (
                            <button
                                key={shot.id}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                className={`mc-relay__shot tap-hit${isActive ? ' is-active' : ''}`}
                                onClick={() => {
                                    setPlaying(false);
                                    setFrame(shot.startFrame);
                                    if (viewMode === 'film' && videoRef.current) {
                                        videoRef.current.currentTime = shot.videoTimeSec;
                                    }
                                }}
                            >
                                <span className="mc-relay__shot-num" style={{ color: shot.accent }}>
                                    #{shot.id}
                                </span>
                                <strong className="mc-relay__shot-name">{shot.superWord}</strong>
                                <span className="mc-relay__shot-place">{shot.place}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default MatchCutCareerRelay;
